import { isNodeJS } from "../../compat";
import THREE from "three";
import { logger } from "../../logger/Logger";
import { USE_OUT_OF_CORE_TILE_MANAGER } from '../globals';
import { LmvBox3 as Box3 } from "./LmvBox3";
import { Vector3 } from "./LmvVector3";
import { BufferAttribute } from "../../imports/avs";
import { convertToUnorm16, decodeUnorm16, decodeOct16, isUnorm16PositionLayout } from './VertexFormatConverter.ts';
import { getVertexCount, makeReadPositionFunc, makeReadNormalFunc } from './VertexEnumerator';

/**
 * Describes one attribute within an interleaved vertex buffer.
 * @typedef {Object} AttributeLayout
 * @property {number} offset - Start position (in floats) of this attribute within one vertex stride.
 * @property {number} itemSize - Number of components per vertex (e.g. 3 for xyz positions).
 * @property {number} [bytesPerItem=4] - Bytes per component (default 4, i.e. float32).
 * @property {boolean} [normalized=false] - Whether integer data should be normalized to [0,1].
 */

/**
 * @typedef {Object} MeshData
 * @property {Float32Array} vb - Vertex buffer containing all interleaved attributes for the mesh
 * @property {Object<string, AttributeLayout>} vblayout - Layout of the buffer attributes
 * @property {Uint16Array|Uint32Array|null} indices - Indices for the faces
 * @property {number} vbstride - Stride between attributes, assuming the Float32Array representation of the ArrayBuffer
 * @property {boolean} [isPoints] - Whether this mesh is rendered as points
 * @property {number} [pointSize] - Point size for point rendering
 * @property {THREE.Box3} [boundingBox] - Optional bounding box
 *
 * @private
 */

/**
 * Reads a decoded vertex attribute (e.g. position or normal) for a single vertex
 * into the provided output vector.
 * @callback VertexAttributeReader
 * @param {number} index - The index of the vertex to read.
 * @param {Vector3} out - The vector that receives the decoded attribute components.
 * @returns {void}
 */

//Finds a precanned BufferAttribute corresponding to the given
//attribute data, so that we don't have to allocate the same exact
//one over and over and over.
var bufattrs = {};

// @todo: misleading name, since the function also crates a buffer attribute if none is cached,
// handles configuration of instancing, and, if incomming attribute layout (not data) is not interleaved
// no cache is skipped (no finding involved at all). This could be split into dedicated tasks.
export function findBufferAttribute(attributeName, attributeData, numInstances) {
  var attr;
  var attrNormalized = attributeData.normalize || attributeData.normalized;
  if (attributeData.array) {
    attr = new BufferAttribute(attributeData.array, attributeData.itemSize);
  } else
  {
    var id = attributeName + "|" +
    attributeData.bytesPerItem + "|" +
    attrNormalized + "|" +
    attributeData.isPattern + "|" +
    attributeData.divisor + "|" +
    attributeData.offset;

    attr = bufattrs[id];
    if (attr)
    return attr;

    attr = new BufferAttribute(undefined, attributeData.itemSize);
    bufattrs[id] = attr;
  }

  attr.normalized = attrNormalized;
  attr.bytesPerItem = attributeData.bytesPerItem;
  attr.isPattern = attributeData.isPattern;

  if (numInstances) {
    attr.divisor = attributeData.divisor;
  }

  if (attributeData.array) {


    //Is the data for the attribute specified separately
    //from the interleaved VB?
  } else if (Object.prototype.hasOwnProperty.call(attributeData, "offset")) {//If the attribute is in the interleaved VB, it has
    //an offset into it.
    attr.offset = attributeData.offset;
    attr.itemOffset = attributeData.offset;
  } else
  {
    logger.warn("VB attribute is neither interleaved nor separate. Something is wrong with the buffer specificaiton.");
  }

  return attr;
}

var attrKeys = {};

function findAttributesKeys(geometry) {
  var key = "";

  for (var p in geometry.attributes)
  key += p + "|";

  var res = attrKeys[key];
  if (res)
  return res;

  res = Object.keys(geometry.attributes);
  attrKeys[key] = res;

  return res;
}

var indexAttr16;
var indexAttr32;
var idcounter = 1;

/**
 * Constructs a new BufferGeometry.
 * @class
 * @alias Autodesk.Viewing.Scene.BufferGeometry
 */
export function LeanBufferGeometry() {

  //Avoid calling the superclass constructor for performance reasons.
  //Skips the creation of a uuid and defining an accessor for the .id property.
  //THREE.BufferGeometry.call(this);

  this.id = idcounter++;

  this.attributes = {};

  // Note:
  //  1. Although __webglInit would also be undefined without this assignment, it is still essential
  //     for performance reasons, because it makes this property known to the JIT compiler. Otherwise,
  //     it would be attached to each buffer later in WebGLRenderer - which would waste performance.
  //  2. It is essential to use "undefined" and not "false" here. The reason is that WebGLRenderer
  //     only checks in the form "__webglInit === undefined", i.e., setting it to "false" here would have
  //     the same effect like setting it to "true" and would finally cause a memory leak.
  this.__webglInit = undefined;

  if (USE_OUT_OF_CORE_TILE_MANAGER) {
    // If the out of core tile manager is used, we will do
    // the whole memory management in the tile manager. All
    // geometry is therefore by default not uploaded and
    // uses streaming draw.
    this.streamingDraw = this.streamingIndex = true;
  }
};

LeanBufferGeometry.prototype = Object.create(THREE.BufferGeometry.prototype);

/**
 * Clones the geometry.
 * @returns {BufferGeometry} The cloned geometry.
 * @alias Autodesk.Viewing.Scene.BufferGeometry#clone
 */
LeanBufferGeometry.prototype.clone = function () {
  // Since Interleaved buffers are handled with some custom logic, the default clone logic fails when
  // cloning BufferAttributes
  const geometry = new LeanBufferGeometry();

  // custom LMV logic
  geometry.ib = this.ib;
  geometry.iblines = this.iblines;
  geometry.vb = this.vb;
  geometry.vbstride = this.vbstride;
  geometry.byteSize = this.byteSize;
  geometry.isLines = this.isLines;
  geometry.isWideLines = this.isWideLines;
  geometry.lineWidth = this.lineWidth;
  geometry.isPoints = this.isPoints;
  geometry.pointSize = this.pointSize;
  geometry.index = this.index;

  for (const attr in this.attributes) {
    const srcAttr = this.attributes[attr];
    const arr = srcAttr.array ? new srcAttr.array.constructor(this.array) : undefined;
    const dstAttr = new BufferAttribute(arr, srcAttr.itemSize);
    dstAttr.offset = srcAttr.offset;
    dstAttr.bytesPerItem = srcAttr.bytesPerItem;
    dstAttr.normalized = srcAttr.normalized;
    geometry.setAttribute(attr, dstAttr);
  }

  if (this.groups) {
    for (var i = 0, il = this.groups.length; i < il; i++) {
      var group = this.groups[i];
      geometry.groups.push({
        start: group.start,
        index: group.index,
        count: group.count });
    }
  }

  return geometry;
};
LeanBufferGeometry.prototype.constructor = LeanBufferGeometry;
LeanBufferGeometry.prototype.isLeanBufferGeometry = true;

/**
 * Computes the accurate size of the geometry in bytes.
 * (the byteSize member ignore the size of the iblines buffer and potentially other buffers in attributes)
 *
 * @returns {number} The size of the geometry in bytes
 * @deprecated Use getByteSize instead.
 */
LeanBufferGeometry.prototype.getAccurateByteSize = function () {
  console.warn('getAccurateByteSize is deprecated. Use getByteSize instead.');
  return this.getByteSize();
};

/**
 * Computes the bounding box of the vertex buffer.
 * @param {Float32Array} vb - The vertex buffer
 * @param {number} posOffset - The offset of the position attribute
 * @param {number} stride - The stride of the vertex buffer
 * @param {Box3} targetBox - The target box
 *
 * @private
 */
const computeBoundingBox = function (vb, posOffset, stride, targetBox) {
  targetBox.makeEmpty();

  const v = new Vector3();
  for (let i = 0; i < vb.length; i += stride) {
    const offset = i + posOffset;
    v.set(vb[offset], vb[offset + 1], vb[offset + 2]);
    targetBox.expandByPoint(v);
  }
};

/**
 * Computes the axis-aligned bounding box of the geometry.
 * @alias Autodesk.Viewing.Scene.BufferGeometry#computeBoundingBox
 */
LeanBufferGeometry.prototype.computeBoundingBox = function () {
  if (this.hash) {// see _computeObjectBounds fast path if .model and .fragId are set
    logger.warn('computeBoundingBox called on OTG geometry. Should use fraglist bounds instead.');
  }

  if (this.boundingBox === null || this.boundingBox === undefined) {
    this.boundingBox = new Box3();
  }

  if (this.vb === null || this.vb === undefined) {
    THREE.BufferGeometry.prototype.computeBoundingBox.call(this);
    return;
  }

  if (this.hasUnorm16Layout()) {
    throw new Error('computeBoundingBox: not supported for UNORM16 layout');
  }

  computeBoundingBox(this.vb, this.attributes.position.offset, this.vbstride, this.boundingBox);
};

/**
 * Computes the bounding sphere of the geometry.
 * @alias Autodesk.Viewing.Scene.BufferGeometry#computeBoundingSphere
 */
LeanBufferGeometry.prototype.computeBoundingSphere = function () {
  if (this.boundingSphere === null || this.boundingSphere === undefined) {
    this.boundingSphere = new THREE.Sphere();
  }

  if (this.vb === null || this.vb === undefined) {
    THREE.BufferGeometry.prototype.computeBoundingSphere.call(this);
    return;
  }

  if (this.hasUnorm16Layout()) {
    throw new Error('computeBoundingSphere: not supported for UNORM16 layout');
  }

  const box = new Box3();
  computeBoundingBox(this.vb, this.attributes.position.offset, this.vbstride, box);

  const center = new Vector3();
  box.getCenter(center);

  const v = new Vector3();
  const vb = this.vb;
  const posOffset = this.attributes.position.offset;
  let maxRadiusSq = 0;
  for (let i = 0; i < vb.length; i += this.vbstride) {
    const offset = i + posOffset;
    v.set(vb[offset], vb[offset + 1], vb[offset + 2]);
    maxRadiusSq = Math.max(maxRadiusSq, center.distanceToSquared(v));
  }

  this.boundingSphere.radius = Math.sqrt(maxRadiusSq);
};

// ---- Public Geometry API ----

/**
 * Sets a named vertex attribute.
 * @param {string} name - Attribute name: 'position', 'normal', 'uv', 'color'.
 * @param {BufferAttribute} attribute - A BufferAttribute wrapping the vertex data.
 * @returns {BufferGeometry} this
 * @throws If geometry is already committed or if setInterleavedBuffer() was used.
 * @alias Autodesk.Viewing.Scene.BufferGeometry#setAttribute
 */
LeanBufferGeometry.prototype.setAttribute = function (name, attribute) {
  if (this.isCommitted()) {
    throw new Error('BufferGeometry: Cannot modify geometry after it has been committed.');
  }

  // Check mutual exclusivity with setInterleavedBuffer
  if (this.vb && this.attributes[name]?.isInterleaved()) {
    throw new Error('BufferGeometry: Cannot call setAttribute() after setInterleavedBuffer(). These are mutually exclusive.');
  }

  // Same-reference no-op
  if (this.attributes[name] === attribute) {
    return this;
  }

  this.attributes[name] = attribute;
  this.attributesKeys = findAttributesKeys(this);

  return this;
};

/**
 * Returns the BufferAttribute for a named attribute.
 * Before commit, returns the original BufferAttribute.
 * After commit, the BufferAttribute data has been processed and might differ from the original.
 * @param {string} name - Attribute name.
 * @returns {BufferAttribute | null}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getAttribute
 */
LeanBufferGeometry.prototype.getAttribute = function (name) {
  const attr = this.attributes[name] || null;
  if (attr?.isInterleaved()) {
    // Return wrapper to allow using the same BufferAttribute interface for interleaved attributes.
    // Other code depends on array being undefined for interleaved attributes, so we can't use the original BufferAttribute directly.
    const result = new BufferAttribute(this.vb, attr.itemSize);
    result.offset = attr.offset;
    result.normalized = attr.normalized;
    result.bytesPerItem = attr.bytesPerItem;
    result.isPattern = attr.isPattern;
    result.divisor = attr.divisor;
    result.stride = this.vbstride;

    return result;
  }

  return attr;
};

/**
 * Returns true if the named attribute is set.
 * @param {string} name - Attribute name.
 * @returns {boolean}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#hasAttribute
 */
LeanBufferGeometry.prototype.hasAttribute = function (name) {
  return this.attributes[name] !== undefined;
};

/**
 * Whether positions and normals are stored in the UNORM16 layout.
 * @returns {boolean}
 * @internal
 */
LeanBufferGeometry.prototype.hasUnorm16Layout = function () {
  return isUnorm16PositionLayout(this.attributes.position);
};

/**
 * Number of vertices, derived from position attribute or interleaved buffer.
 * @returns {number}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getVertexCount
 */
LeanBufferGeometry.prototype.getVertexCount = function () {
  return getVertexCount(this);
};

/**
 * Sets a pre-interleaved vertex buffer with attribute layout.
 * Mutually exclusive with setAttribute() — mixing the two throws.
 * @param {Float32Array} data - Interleaved vertex buffer.
 * @param {number} stride - Number of float32 elements per vertex.
 * @param {Object.<string, AttributeLayout>} layout - Map of attribute names to their layouts.
 * @returns {BufferGeometry} this
 * @throws If geometry is already committed or if setAttribute() was used.
 * @alias Autodesk.Viewing.Scene.BufferGeometry#setInterleavedBuffer
 */
LeanBufferGeometry.prototype.setInterleavedBuffer = function (data, stride, layout, numInstances) {
  if (this.isCommitted()) {
    throw new Error('BufferGeometry: Cannot modify geometry after it has been committed.');
  }

  this.vb = data;
  this.vbstride = stride;

  for (const attrName in layout) {
    const desc = layout[attrName];

    if (this.attributes[attrName] && !this.attributes[attrName].isInterleaved()) {
      throw new Error('BufferGeometry: Cannot call setInterleavedBuffer() after setAttribute(). These are mutually exclusive.');
    }

    this.attributes[attrName] = findBufferAttribute(attrName, {
      offset: desc.offset,
      itemSize: desc.itemSize,
      bytesPerItem: desc.bytesPerItem || 4,
      normalized: desc.normalized || false,
      isPattern: desc.isPattern,
      divisor: desc.divisor,
      // In some cases (e.g F2D on mobile), the attribute layout can have a mix of interleaved and non-interleaved attributes.
      // In those cases, we need to provide the non-interleaved array for the non-interleaved attributes
      array: desc.array
    }, numInstances);
  }
  this.attributesKeys = findAttributesKeys(this);
  this.vbbuffer = undefined;
  this.vbNeedsUpdate = true;

  return this;
};

/**
 * Sets the index buffer of the geometry.
 * @param {Uint16Array|Uint32Array} data - Index buffer.
 * @returns {BufferGeometry} this
 * @throws If geometry is already committed.
 * @alias Autodesk.Viewing.Scene.BufferGeometry#setIndices
 */
LeanBufferGeometry.prototype.setIndices = function (data) {
  if (this.isCommitted()) {
    throw new Error('BufferGeometry: Cannot modify geometry after it has been committed.');
  }

  if (!indexAttr16) initBufferGeometry();

  this.ib = data;
  this.index = data instanceof Uint32Array ? indexAttr32 : indexAttr16;
  this.ibbuffer = undefined;

  return this;
};

/**
 * Sets the index buffer of the geometry.
 * @param {Uint16Array|Uint32Array} data - Index buffer.
 * @returns {BufferGeometry} this
 * @throws If geometry is already committed.
 *
 * @deprecated Use setIndices instead. Kept for backward compatibility with THREE.js
 *
 * @private
 */
LeanBufferGeometry.prototype.setIndex = LeanBufferGeometry.prototype.setIndices;

/**
 * Returns the index buffer, or null.
 * @returns {Uint16Array|Uint32Array|null}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getIndices
 */
LeanBufferGeometry.prototype.getIndices = function () {
  return this.ib || null;
};

/**
 * Returns index buffer, or null.
 * @returns {Uint16Array|Uint32Array|null}
 *
 * @deprecated Use getIndices instead. Kept for backward compatibility with THREE.js
 */
LeanBufferGeometry.prototype.getIndex = LeanBufferGeometry.prototype.getIndices;

/**
 * Sets edge wireframe indices. Every 2 consecutive values form a line segment.
 * @param {Uint16Array|Uint32Array} data - Edge index buffer.
 * @returns {BufferGeometry} this
 * @throws If geometry is already committed.
 * @alias Autodesk.Viewing.Scene.BufferGeometry#setEdgeIndices
 */
LeanBufferGeometry.prototype.setEdgeIndices = function (data) {
  if (this.isCommitted()) {
    throw new Error('BufferGeometry: Cannot modify geometry after it has been committed.');
  }

  if (!indexAttr16) initBufferGeometry();

  this.iblines = data;
  this.iblinesbuffer = undefined;

  this.attributes.indexlines = data instanceof Uint32Array ? indexAttr32 : indexAttr16;
  this.attributesKeys = findAttributesKeys(this);

  return this;
};

/**
 * Returns edge index buffer, or null.
 * @returns {Uint16Array|Uint32Array|null}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getEdgeIndices
 */
LeanBufferGeometry.prototype.getEdgeIndices = function () {
  return this.iblines || null;
};

/**
 * Number of edge line segments (edgeIndices.length / 2).
 * @returns {number}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getEdgeCount
 */
LeanBufferGeometry.prototype.getEdgeCount = function () {
  return this.iblines ? this.iblines.length / 2 | 0 : 0;
};

/**
 * Returns true after the geometry has been added to an InstanceCollection3D.
 * @returns {boolean}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#isCommitted
 */
LeanBufferGeometry.prototype.isCommitted = function () {
  return this.svfid !== undefined;
};

/**
 * Total byte size of all buffers.
 * @returns {number}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getByteSize
 */
LeanBufferGeometry.prototype.getByteSize = function () {
  let result = 0;

  result += this.vb?.byteLength ?? (this.vbLength !== undefined ? this.vbLength * 4 : 0);
  result += this.ib?.byteLength ?? (this.ibLength !== undefined ? this.ibLength * this.ibByteSize : 0);
  result += this.iblines?.byteLength ?? (this.iblinesLength !== undefined ? this.iblinesLength * this.iblinesByteSize : 0);

  for (let attrib of Object.values(this.attributes)) {
    result += attrib.array?.byteLength ?? (attrib.arrayLength ? attrib.arrayLength * attrib.arrayByteSize : 0);
  }

  return result;
};

/**
 * Explicitly sets the bounding box.
 * @param {Vector3} min
 * @param {Vector3} max
 * @returns {BufferGeometry} this
 * @alias Autodesk.Viewing.Scene.BufferGeometry#setBoundingBox
 */
LeanBufferGeometry.prototype.setBoundingBox = function (min, max) {
  if (!this.boundingBox) {
    this.boundingBox = new Box3();
  }
  this.boundingBox.min.copy(min);
  this.boundingBox.max.copy(max);
  return this;
};

/**
 * Returns the bounding box. If target is provided, copies into it, otherwise returns a clone.
 * Returns null if neither computed nor set.
 * @param {Box3} [target]
 * @returns {Box3|null}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getBoundingBox
 */
LeanBufferGeometry.prototype.getBoundingBox = function (target) {
  if (!this.boundingBox) return null;
  if (target) {
    target.copy(this.boundingBox);
    return target;
  }
  return this.boundingBox.clone();
};

/**
 * Returns true if the geometry uses an interleaved vertex buffer.
 * @returns {boolean}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#isInterleaved
 */
LeanBufferGeometry.prototype.isInterleaved = function () {
  return !!this.vb;
};

/**
 * Returns a function(vertexIndex, outVec3) that reads a decoded position into outVec3.
 * @returns {VertexAttributeReader|null}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getPositionReader
 */
LeanBufferGeometry.prototype.getPositionReader = function () {
  return makeReadPositionFunc(this);
};

/**
 * Returns a function(vertexIndex, outVec3) that reads a decoded normal into outVec3.
 * @returns {VertexAttributeReader|null}
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getNormalReader
 */
LeanBufferGeometry.prototype.getNormalReader = function () {
  return makeReadNormalFunc(this);
};

/**
 * Decodes a normal from the vertex buffer.
 * For performance critical readbacks of large parts of the geometry use getNormalReader instead.
 * @param {number} index - The index of the vertex.
 * @param {Vector3} target - The target vector to store the decoded normal.
 * @returns {Vector3|null} The decoded normal or null if the normal is not set.
 *
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getNormal
 */
LeanBufferGeometry.prototype.getNormal = function (index, target) {
  const attributes = this.attributes;
  const pattr = this.vblayout ? this.vblayout.position : attributes?.position || null;

  if (isUnorm16PositionLayout(pattr) && this.vb) {
    // creating new Uint16Array view on every call. For performance critical readbacks use getNormalReader instead.
    const u16 = new Uint16Array(this.vb.buffer, this.vb.byteOffset, this.vb.length * 2);
    decodeOct16(u16[index * this.vbstride * 2 + 3], target);
    return target;
  }

  const nattr = this.vblayout ? this.vblayout.normal : attributes?.normal || null;
  if (!nattr) return null;
  if (!nattr.array && (nattr.itemSize !== 3 || nattr.bytesPerItem !== 4)) return null;

  const normals = this.vb || nattr.array;
  const p = index * (this.vb ? this.vbstride : 3) + (nattr.offset || 0);
  target.x = normals[p];
  target.y = normals[p + 1];
  target.z = normals[p + 2];
  return target;
};

/**
 * Decodes a position from the vertex buffer.
 * For performance critical readbacks of large parts of the geometry use getPositionReader instead.
 * @param {number} index - The index of the vertex.
 * @param {Vector3} target - The target vector to store the decoded position.
 * @returns {Vector3|null} The decoded position or null if the position is not set.
 * @alias Autodesk.Viewing.Scene.BufferGeometry#getPosition
 */
LeanBufferGeometry.prototype.getPosition = function (index, target) {
  const attributes = this.attributes;

  // Resolve the position attribute descriptor. Be careful, 2D buffers
  // don't use the 'position' attribute for positions. Reject those.
  const pattr = this.vblayout ? this.vblayout.position : attributes.position || null;

  if (!pattr) return null;

  if (isUnorm16PositionLayout(pattr) && this.vb) {
    // creating new Uint16Array view on every call. For performance critical readbacks use getPositionReader instead.
    const u16 = new Uint16Array(this.vb.buffer, this.vb.byteOffset, this.vb.length * 2);
    const p = index * this.vbstride * 2;
    target.x = decodeUnorm16(u16[p]);
    target.y = decodeUnorm16(u16[p + 1]);
    target.z = decodeUnorm16(u16[p + 2]);
  } else {
    const positions = this.vb || pattr.array;
    const p = index * (this.vb ? this.vbstride : 3) + (pattr.offset || 0);
    target.x = positions[p];
    target.y = positions[p + 1];
    target.z = positions[p + 2];
  }
  return target;
};

function initBufferGeometry() {

  indexAttr16 = new BufferAttribute(undefined, 1);
  indexAttr16.bytesPerItem = 2;

  indexAttr32 = new BufferAttribute(undefined, 1);
  indexAttr32.bytesPerItem = 4;
}

export function createBufferGeometry(instanced) {
  if (!indexAttr16) {
    initBufferGeometry();
  }

  return new LeanBufferGeometry();
}

/**
 * Converts a mesh description passed back from worker threads into a renderable three.js
 * compatible BufferGeometry.
 * Sets various extra flags we need.
 * @param {Object} mdata
 * @param {MeshData} mdata.mesh
 * @returns {LeanBufferGeometry}
 *
 * @internal
 *
 * TODO remove with typedoc
 * @private
 */
export function meshToGeometry(mdata) {

  var mesh = mdata.mesh;
  var geometry = createBufferGeometry(mesh.numInstances);

  if (isNodeJS()) {
    //Used by SVF post-processing tools
    geometry.packId = mdata.packId;
    geometry.meshIndex = mdata.meshIndex;
  }

  geometry.byteSize = 0;

  geometry.setInterleavedBuffer(mesh.vb, mesh.vbstride, mesh.vblayout, mesh.numInstances);

  geometry.byteSize += mesh.vb.byteLength;
  geometry.hash = mdata.hash;

  if (mesh.isLines) /* mesh is SVF lines */
    geometry.isLines = mesh.isLines;
  if (mesh.isWideLines) {/* mesh is SVF wide lines */
    geometry.isWideLines = true;
    geometry.lineWidth = mesh.lineWidth;
  }
  if (mesh.isPoints) {/* mesh is SVF points */
    geometry.isPoints = mesh.isPoints;
    geometry.pointSize = mesh.pointSize;
  }
  if (mdata.is2d) /* mesh is from F2D */{
      geometry.is2d = true;
    }
  geometry.numInstances = mesh.numInstances;

  // Having an index buffer is optional for point cloud rendering with WebGPU.
  if (mesh.indices) {
    geometry.setIndices(mesh.indices);

    // @todo: this is most likely the wrong byte count. Make this a lazy query, computing the size on the fly and caching
    // the value for as long as the geometry does not change. To correctly account for ib and iblines? we should use
    // geometry.ib.byteLength + geometry.iblines.byteLength and
    // geometry.index.array.byteLength + geometry.indexlines.array.byteLength respectively.
    // Since there is no comment on what byteSize is expected to measure, this might be as well correct as is.
    geometry.byteSize += mesh.indices.byteLength;
  }

  if (mesh.iblines) {
    geometry.setEdgeIndices(mesh.iblines);
  }

  //TODO: Not sure chunking into list of smaller offset/counts
  //is required for LMV data since it's already broken up.
  //if (mesh.indices.length > 65535)
  // Works fine now. Left in for debugging.
  //if (mesh.vb.length / mesh.vbstride > 65535)
  //    logger.warn("Mesh with " + (mesh.vb.length / mesh.vbstride) + " > 65535 vertices. It will fail to draw.");

  //TODO: This is a transient object that gets freed once the geometry
  //is added to the GeometryList. We can save on the object creation
  //eventually when we do micro optimizations.
  if (mesh.boundingBox) {
    geometry.boundingBox = new THREE.Box3().copy(mesh.boundingBox);
  }
  if (mesh.boundingSphere) {
    geometry.boundingSphere = new THREE.Sphere().copy(mesh.boundingSphere);
  }

  return geometry;
}


//Combines geometry with multiple vertex attribute channels into single interleaved buffer
//and generates new the geometry buffer attributes accordingly
function interleaveGeometry(geometry, packNormals) {

  if (geometry.vb && !geometry.vbNeedsUpdate) {
    return;
  }

  let attributes = {};
  let curOffset = 0;
  let oldAttrs = geometry.attributes;

  let position = oldAttrs.position;
  if (position) {
    attributes.position = findBufferAttribute("position", { offset: curOffset, bytesPerItem: 4, itemSize: 3 });
    curOffset += 3;
  }

  let normal = oldAttrs.normal;
  if (normal) {
    if (packNormals || normal.bytesPerItem === 2) {
      attributes.normal = findBufferAttribute("normal", { offset: curOffset, bytesPerItem: 2, itemSize: 2, normalized: true });
      curOffset += 1;
    } else {
      attributes.normal = findBufferAttribute("normal", { offset: curOffset, bytesPerItem: 4, itemSize: 3, normalized: normal.normalized });
      curOffset += 3;
    }
  }

  let uv = oldAttrs.uv;
  if (uv) {
    attributes.uv = findBufferAttribute("uv", { offset: curOffset, bytesPerItem: 4, itemSize: 2 });
    curOffset += 2;
  }

  let color = oldAttrs.color;
  let isFloatColor = false;
  let isByteColor = false;
  if (color) {
    // Note: We only support either 4-Byte or 3-float per vertex
    isFloatColor = color.array instanceof Float32Array && color.itemSize === 3;
    isByteColor = color.array instanceof Uint8Array;
    if (isFloatColor || isByteColor) {
      const bytesPerItem = isFloatColor ? 4 : 1;
      attributes.color = findBufferAttribute("color", {
        offset: curOffset,
        bytesPerItem,
        itemSize: color.itemSize,
        normalized: color.normalized
      });

      // Offset is in 32-bit steps. So either 3-floats or 4-bytes
      // (assuming that byteColors are always itemSize 4).
      curOffset += isFloatColor ? 3 : 1;
    } else {
      logger.warn("Skipped unsupported vertex-color type. Must be Float3 or Uint32-Colors");
    }
  }

  let stride = curOffset;
  let numVerts = position.array.length / (position.array.itemSize || 3);
  let vb = new Float32Array(numVerts * stride);
  let vbU = new Uint16Array(vb.buffer);

  if (position) {
    let pos = 0;
    let src = position.array;
    for (let i = 0; i < numVerts; i++, pos += stride) {
      vb[pos] = src[i * 3];
      vb[pos + 1] = src[i * 3 + 1];
      vb[pos + 2] = src[i * 3 + 2];
    }
  }

  if (normal) {
    if (normal.itemSize === 2) {
      //normals are already packed
      let pos = attributes.normal.offset * 2; //12 bytes offset into the vertex data, expressed in uint16s (2 bytes per)
      let src = normal.array;
      for (let i = 0; i < numVerts; i++, pos += stride * 2) {
        vbU[pos] = src[i * 2];
        vbU[pos + 1] = src[i * 2 + 1];
      }
    } else {
      //input normals are not packed
      if (!packNormals) {
        let pos = attributes.normal.offset;
        let src = normal.array;
        for (let i = 0; i < numVerts; i++, pos += stride) {
          vb[pos] = src[i * 3];
          vb[pos + 1] = src[i * 3 + 1];
          vb[pos + 2] = src[i * 3 + 2];
        }
      } else {
        let pos = attributes.normal.offset * 2; //12 bytes offset into the vertex data, expressed in uint16s (2 bytes per)
        let src = normal.array;
        for (let i = 0; i < numVerts; i++, pos += stride * 2) {
          let nx = src[i * 3];
          let ny = src[i * 3 + 1];
          let nz = src[i * 3 + 2];

          vbU[pos] = (Math.atan2(ny, nx) / Math.PI + 1.0) * 0.5 * 65535 | 0;
          vbU[pos + 1] = (nz + 1.0) * 0.5 * 65535 | 0;
        }
      }
    }
  }

  if (uv) {
    let pos = attributes.uv.offset;
    let src = uv.array;
    for (let i = 0; i < numVerts; i++, pos += stride) {
      vb[pos] = src[i * 2];
      vb[pos + 1] = src[i * 2 + 1];
    }
  }

  if (color) {
    let src = color.array;
    if (isFloatColor) {
      let pos = attributes.color.offset;
      for (let i = 0; i < numVerts; i++, pos += stride) {
        vb[pos] = src[i * 3];
        vb[pos + 1] = src[i * 3 + 1];
        vb[pos + 2] = src[i * 3 + 2];
      }
    } else if (isByteColor) {
      // get uint8 view to write bytes into interleaved vertex buffer
      let vbUint8 = new Uint8Array(vb.buffer, vb.byteOffset, vb.byteLength);

      // get stride and startOffset in bytes instead of floats
      let byteStride = stride * 4;
      let pos = attributes.color.offset * 4;

      for (let i = 0; i < numVerts; i++, pos += byteStride) {
        vbUint8[pos] = src[i * 4];
        vbUint8[pos + 1] = src[i * 4 + 1];
        vbUint8[pos + 2] = src[i * 4 + 2];
        vbUint8[pos + 3] = src[i * 4 + 3];
      }
    }

  }

  //TODO: Add support for 32bit indices
  // May not exist yet if createBufferGeometry() was not called before.
  if (!indexAttr16) {
    initBufferGeometry();
  }
  attributes.index = indexAttr16;

  let ib;
  if (geometry.ib) {
    // Index buffer already set directly (e.g., via setIndices)
    ib = geometry.ib;
    attributes.index = ib instanceof Uint32Array ? indexAttr32 : indexAttr16;
  } else if (geometry.attributes.index) {
    ib = oldAttrs.index.array;
  } else {
    //generate trivial index buffer
    ib = new Uint16Array(numVerts);
    for (let i = 0; i < numVerts; i++) {
      ib[i] = i;
    }
  }

  let iblines;
  if (geometry.iblines) {
    // Edge index buffer already set directly (e.g., via setEdgeIndices)
    iblines = geometry.iblines;
    attributes.indexlines = iblines instanceof Uint32Array ? indexAttr32 : indexAttr16;
  } else if (geometry.attributes.indexlines) {
    iblines = oldAttrs.indexlines.array;
    attributes.indexlines = indexAttr16;
  }

  //TODO: this may need to be more delicate in case we want to allow dynamic modification
  geometry.vbstride = stride;
  geometry.vb = vb;
  geometry.ib = ib;
  geometry.iblines = iblines;
  geometry.attributesInterleaved = attributes;
  geometry.vbNeedsUpdate = false;
}

export let BufferGeometryUtils = {
  meshToGeometry: meshToGeometry,
  createBufferGeometry: createBufferGeometry,
  findBufferAttribute: findBufferAttribute,
  interleaveGeometry,
  convertToUnorm16,
  decodeOct16
};

export const BufferGeometry = LeanBufferGeometry;